import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Chip,
  ConfirmDeleteModal,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import { PageHeader } from '../components/PageHeader'
import { useOperators } from '../api/hooks'
import { api, ApiError } from '../api/client'
import type { Operator } from '../api/types'
import { useAuthStore } from '../stores/auth'
import { initials, relativeTime } from '../lib/format'
import { parseAllowedIpsInput } from '../lib/ipacl'

const USER_ROLES = ['admin', 'operator', 'viewer']
const SERVICE_ROLES = ['operator', 'viewer']

function detail(err: unknown): string {
  return err instanceof ApiError ? err.detail : err instanceof Error ? err.message : String(err)
}

function roleTone(role: string): 'gold' | 'info' | 'muted' {
  if (role === 'admin') return 'gold'
  if (role === 'operator') return 'info'
  return 'muted'
}

/** Mono secret box with copy button — secrets are only shown once. */
function SecretBox({ label, secret }: { label: string; secret: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="rounded-sm border border-ostrel-warn/50 bg-ostrel-warn/10 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-warn">{label}</p>
      <div className="mt-2 flex items-center gap-2">
        <code className="tnum min-w-0 flex-1 truncate rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-1.5 font-mono text-xs text-ostrel-ink">
          {secret}
        </code>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void navigator.clipboard?.writeText(secret).then(() => {
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            })
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-ostrel-ink-muted">
        Shown once — store it somewhere safe. It cannot be retrieved again.
      </p>
    </div>
  )
}

const selectClass =
  'rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-1 text-xs text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold disabled:opacity-60'

const textareaClass =
  'tnum rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 font-mono text-xs text-ostrel-ink placeholder:text-ostrel-ink-muted focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold'

/** One-CIDR-per-line editor with inline validation (src/lib/ipacl.ts mirrors
 *  the backend's tenancy/ipacl.py rules, including the /22 v4 and /64 v6
 *  width caps). `required` (service accounts) refuses an empty list. Distinct
 *  from components/AllowedIpsField (the gateways editor): the copy and the
 *  empty-list rule differ — for service accounts the allowlist is MANDATORY.
 */
function AllowlistField({
  id,
  value,
  onChange,
  required,
}: {
  id: string
  value: string
  onChange: (v: string) => void
  required: boolean
}) {
  const { cidrs, error } = parseAllowedIpsInput(value)
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        Allowed IPs (one CIDR per line{required ? '' : ', optional'})
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        spellCheck={false}
        placeholder={'203.0.113.0/24\n2001:db8::/64'}
        className={textareaClass}
      />
      <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
        {required
          ? 'Service accounts can only log in from listed IPs — at least one CIDR is required.'
          : 'Users can log in from any IP when this list is empty.'}{' '}
        Widest allowed network: /22 (IPv4) or /64 (IPv6); a bare IP pins a single address.
      </p>
      {required && cidrs.length === 0 && (
        <p className="text-xs text-ostrel-danger">At least one CIDR is required for a service account.</p>
      )}
      {error && <p className="text-xs text-ostrel-danger">{error}</p>}
    </div>
  )
}

export default function OperatorsPage() {
  const customer = useAuthStore((s) => s.customer)
  const principal = useAuthStore((s) => s.principal)
  const scope = customer?.unique_id ?? 'none'
  const queryClient = useQueryClient()
  const myRole = customer?.role ?? ''
  const amIAdmin = myRole === 'admin'

  const operators = useOperators()
  const endpointMissing = operators.isError && operators.error instanceof ApiError && operators.error.status === 404

  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteFirst, setInviteFirst] = useState('')
  const [inviteLast, setInviteLast] = useState('')
  const [inviteRole, setInviteRole] = useState('operator')
  const [inviteSecret, setInviteSecret] = useState<string | null>(null)

  const [serviceOpen, setServiceOpen] = useState(false)
  const [serviceName, setServiceName] = useState('')
  const [serviceRole, setServiceRole] = useState('operator')
  const [serviceIps, setServiceIps] = useState('')
  const [serviceSecret, setServiceSecret] = useState<string | null>(null)

  const [ipsTarget, setIpsTarget] = useState<Operator | null>(null)
  const [ipsText, setIpsText] = useState('')

  const [removeTarget, setRemoveTarget] = useState<Operator | null>(null)
  const [transferTarget, setTransferTarget] = useState<Operator | null>(null)
  const [tableError, setTableError] = useState<string | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope, 'operators'] })
  const onTableError = (err: unknown) => setTableError(detail(err))

  const invite = useMutation({
    mutationFn: api.inviteOperator,
    onSuccess: (res) => {
      setInviteSecret(res.setup_secret)
      invalidate()
    },
  })
  const createService = useMutation({
    mutationFn: ({ name, role, allowedIps }: { name: string; role: string; allowedIps: string[] }) =>
      api.createServiceAccount(name, role, allowedIps),
    onSuccess: (res) => {
      setServiceSecret(res.secret)
      invalidate()
    },
  })
  const setAllowedIps = useMutation({
    mutationFn: ({ uid, allowedIps }: { uid: string; allowedIps: string[] }) =>
      api.setOperatorAllowedIps(uid, allowedIps),
    onSuccess: () => {
      setIpsTarget(null)
      invalidate()
    },
  })
  const setRole = useMutation({
    mutationFn: ({ uid, role }: { uid: string; role: string }) => api.setOperatorRole(uid, role),
    onSuccess: invalidate,
    onError: onTableError,
  })
  const remove = useMutation({
    mutationFn: api.removeOperator,
    onSuccess: () => {
      setRemoveTarget(null)
      invalidate()
    },
  })
  const transfer = useMutation({
    mutationFn: api.transferOwnership,
    onSuccess: () => {
      setTransferTarget(null)
      invalidate()
    },
  })
  const rotate = useMutation({
    mutationFn: api.rotateServiceAccount,
    onSuccess: (res) => setServiceSecret(res.secret),
  })

  const closeInvite = () => {
    setInviteOpen(false)
    setInviteSecret(null)
    setInviteEmail('')
    setInviteFirst('')
    setInviteLast('')
    setInviteRole('operator')
    invite.reset()
  }
  const closeService = () => {
    setServiceOpen(false)
    setServiceSecret(null)
    setServiceName('')
    setServiceRole('operator')
    setServiceIps('')
    createService.reset()
    rotate.reset()
  }
  const openIps = (o: Operator) => {
    setAllowedIps.reset()
    setIpsTarget(o)
    setIpsText(o.allowed_ips.join('\n'))
  }
  const closeIps = () => {
    setIpsTarget(null)
    setAllowedIps.reset()
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Access administration"
        title="Operators"
        action={
          <>
            <Button variant="primary" size="md" onClick={() => setInviteOpen(true)} disabled={endpointMissing}>
              + Invite operator
            </Button>
            <Button
              variant="ghost"
              size="md"
              disabled={endpointMissing}
              onClick={() => {
                createService.reset()
                rotate.reset()
                setServiceSecret(null)
                setServiceOpen(true)
              }}
            >
              + Service account
            </Button>
          </>
        }
      />

      {operators.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : endpointMissing ? (
        <EmptyState
          title="Operators endpoint not available yet"
          hint="The backend operators API is still being rolled out — check back shortly."
        />
      ) : operators.isError ? (
        <EmptyState title="Could not load operators" hint={operators.error.message} />
      ) : operators.data.length === 0 ? (
        <EmptyState title="No operators" hint="Invite the first operator to administer this customer." />
      ) : (
        <>
          {operators.data.some((o) => o.kind === 'service' && !o.ip_allowlist_set) && (
            <div
              role="alert"
              className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3.5 py-3"
            >
              <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-danger">
                Service account login blocked
              </p>
              <p className="mt-1 text-xs leading-relaxed text-ostrel-ink">
                Service accounts can only log in from listed IPs.{' '}
                {operators.data
                  .filter((o) => o.kind === 'service' && !o.ip_allowlist_set)
                  .map((o) => o.name)
                  .join(', ')}{' '}
                has no IP allowlist — logins are denied until one is set.
              </p>
            </div>
          )}
        <div className="rounded-sm border border-ostrel-line bg-ostrel-card">
          <Table>
            <THead>
              <TR>
                <TH>Principal</TH>
                <TH>Kind</TH>
                <TH>Username / email</TH>
                <TH>Role</TH>
                <TH>Status</TH>
                <TH>Last active</TH>
                <TH className="text-right">Manage</TH>
              </TR>
            </THead>
            <TBody>
              {operators.data.map((o) => {
                const isSelf = o.principal_unique_id === principal?.unique_id
                const locked = o.is_owner || isSelf
                return (
                  <TR key={o.unique_id} className={o.active ? '' : 'opacity-60'}>
                    <TD>
                      <span className="flex items-center gap-2.5">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border border-ostrel-line bg-ostrel-sand text-[10px] font-semibold text-ostrel-ink-muted">
                          {initials(o.name || '?')}
                        </span>
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate text-sm font-medium text-ostrel-ink">{o.name}</span>
                            {o.is_owner && <Chip tone="gold" label="Owner" />}
                          </span>
                          <span className="tnum block font-mono text-[10px] text-ostrel-ink-muted">
                            {o.unique_id}
                          </span>
                        </span>
                      </span>
                    </TD>
                    <TD>
                      <Chip tone="muted" label={o.kind === 'service' ? 'Service account' : 'User'} />
                    </TD>
                    <TD>
                      <span className="tnum block truncate font-mono text-xs text-ostrel-ink-muted">
                        {o.email || o.username || '—'}
                      </span>
                    </TD>
                    <TD>
                      {locked ? (
                        <Chip tone={roleTone(o.role)} label={o.role} />
                      ) : (
                        <select
                          aria-label={`Role for ${o.name}`}
                          value={o.role}
                          disabled={setRole.isPending}
                          onChange={(e) => {
                            setTableError(null)
                            setRole.mutate({ uid: o.unique_id, role: e.target.value })
                          }}
                          className={selectClass}
                        >
                          {(o.kind === 'service' ? SERVICE_ROLES : USER_ROLES).map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      )}
                    </TD>
                    <TD>
                      <span className="flex items-center gap-1.5">
                        <Chip tone={o.active ? 'ok' : 'danger'} label={o.active ? 'Active' : 'Suspended'} />
                        {o.kind === 'service' && !o.ip_allowlist_set && (
                          <Chip tone="danger" label="Login blocked" />
                        )}
                      </span>
                    </TD>
                    <TD>
                      <span className="tnum text-xs text-ostrel-ink-muted">
                        {o.last_login ? relativeTime(o.last_login) : 'never'}
                      </span>
                    </TD>
                    <TD className="text-right">
                      <span className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openIps(o)}>
                          IP allowlist
                        </Button>
                        {o.kind === 'service' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={rotate.isPending}
                            onClick={() => {
                              setServiceOpen(true)
                              setServiceSecret(null)
                              rotate.mutate(o.unique_id)
                            }}
                          >
                            Rotate key
                          </Button>
                        )}
                        {amIAdmin && o.kind === 'user' && !o.is_owner && !isSelf && (
                          <Button variant="ghost" size="sm" onClick={() => setTransferTarget(o)}>
                            Make owner
                          </Button>
                        )}
                        {o.is_owner ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-ostrel-ink-muted">
                            Transfer ownership first
                          </span>
                        ) : (
                          !isSelf && (
                            <Button variant="danger" size="sm" onClick={() => setRemoveTarget(o)}>
                              Remove
                            </Button>
                          )
                        )}
                      </span>
                    </TD>
                  </TR>
                )
              })}
            </TBody>
          </Table>
        </div>
        </>
      )}

      {tableError && <p className="text-xs text-ostrel-danger">{tableError}</p>}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {[
          ['Owner (flag)', 'A flag on one grant per customer, not a role — always exactly one, always a user. It rides on top of the grant role and can only move by transfer.'],
          ['Administrator', 'Full control: connectors, operators, locations, remote unlock.'],
          ['Operator', 'Day-to-day: triage credentials, block/unblock, remote unlock.'],
          ['Viewer', 'Read-only across people, credentials, events and locations.'],
        ].map(([title, text]) => (
          <div key={title} className="rounded-sm border border-ostrel-line bg-ostrel-card p-3.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold">{title}</p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-ostrel-ink-muted">{text}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-ostrel-ink-muted">
        Service accounts are capped at operator or viewer and can never hold the owner flag.
      </p>

      <Modal open={inviteOpen} title="Invite operator" onClose={closeInvite}>
        {inviteSecret ? (
          <div className="flex flex-col gap-4">
            <p className="text-xs leading-relaxed text-ostrel-ink-muted">
              Invitation created. Hand this setup secret to the operator — it completes their first sign-in.
            </p>
            <SecretBox label="Setup secret" secret={inviteSecret} />
            <div className="flex justify-end">
              <Button variant="navy" size="sm" onClick={closeInvite}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              invite.mutate({
                email: inviteEmail,
                role: inviteRole,
                ...(inviteFirst ? { first_name: inviteFirst } : {}),
                ...(inviteLast ? { last_name: inviteLast } : {}),
              })
            }}
          >
            <Input
              label="Email"
              type="email"
              required
              placeholder="name@company.se"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input label="First name" value={inviteFirst} onChange={(e) => setInviteFirst(e.target.value)} />
              <Input label="Last name" value={inviteLast} onChange={(e) => setInviteLast(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="invite-role" className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                Role
              </label>
              <select id="invite-role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className={selectClass}>
                {USER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            {invite.isError && <p className="text-xs text-ostrel-danger">{detail(invite.error)}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={closeInvite}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" size="sm" disabled={invite.isPending || !inviteEmail}>
                {invite.isPending ? 'Sending…' : 'Send invite'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={serviceOpen} title="Service account" onClose={closeService}>
        {serviceSecret ? (
          <div className="flex flex-col gap-4">
            <p className="text-xs leading-relaxed text-ostrel-ink-muted">
              {rotate.isSuccess ? 'Key rotated — the previous secret stops working immediately.' : 'Service account created.'}
            </p>
            <SecretBox label="Secret" secret={serviceSecret} />
            <div className="flex justify-end">
              <Button variant="navy" size="sm" onClick={closeService}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              const { cidrs, error } = parseAllowedIpsInput(serviceIps)
              if (error || cidrs.length === 0) return
              createService.mutate({ name: serviceName, role: serviceRole, allowedIps: cidrs })
            }}
          >
            <Input
              label="Name"
              required
              placeholder="Fastum ERP sync"
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
            />
            <AllowlistField id="service-ips" value={serviceIps} onChange={setServiceIps} required />
            <div className="flex flex-col gap-1.5">
              <label htmlFor="service-role" className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                Role
              </label>
              <select id="service-role" value={serviceRole} onChange={(e) => setServiceRole(e.target.value)} className={selectClass}>
                {SERVICE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            {createService.isError && <p className="text-xs text-ostrel-danger">{detail(createService.error)}</p>}
            {rotate.isError && <p className="text-xs text-ostrel-danger">{detail(rotate.error)}</p>}
            {rotate.isPending && <p className="text-xs text-ostrel-ink-muted">Rotating key…</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={closeService}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={
                  createService.isPending ||
                  !serviceName ||
                  parseAllowedIpsInput(serviceIps).cidrs.length === 0 ||
                  parseAllowedIpsInput(serviceIps).error !== null
                }
              >
                {createService.isPending ? 'Creating…' : 'Create account'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDeleteModal
        open={removeTarget !== null}
        title="Remove operator"
        objectName={removeTarget?.name ?? ''}
        expected={removeTarget?.unique_id ?? ''}
        pending={remove.isPending}
        error={remove.isError ? detail(remove.error) : null}
        confirmLabel={remove.isPending ? 'Removing…' : 'Remove'}
        onConfirm={() => removeTarget && remove.mutate(removeTarget.unique_id)}
        onClose={() => setRemoveTarget(null)}
      >
        <p>
          Remove <span className="font-medium">{removeTarget?.name}</span>? Their access to this
          customer is revoked immediately.
        </p>
      </ConfirmDeleteModal>

      <Modal
        open={ipsTarget !== null}
        title={`IP allowlist — ${ipsTarget?.name ?? ''}`}
        onClose={closeIps}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (!ipsTarget) return
            const { cidrs, error } = parseAllowedIpsInput(ipsText)
            if (error || (ipsTarget.kind === 'service' && cidrs.length === 0)) return
            setAllowedIps.mutate({ uid: ipsTarget.unique_id, allowedIps: cidrs })
          }}
        >
          <AllowlistField
            id="edit-ips"
            value={ipsText}
            onChange={setIpsText}
            required={ipsTarget?.kind === 'service'}
          />
          {setAllowedIps.isError && <p className="text-xs text-ostrel-danger">{detail(setAllowedIps.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={closeIps}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={
                setAllowedIps.isPending ||
                parseAllowedIpsInput(ipsText).error !== null ||
                (ipsTarget?.kind === 'service' && parseAllowedIpsInput(ipsText).cidrs.length === 0)
              }
            >
              {setAllowedIps.isPending ? 'Saving…' : 'Save allowlist'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={transferTarget !== null} title="Transfer ownership" onClose={() => setTransferTarget(null)}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ostrel-ink">
            Make <span className="font-medium">{transferTarget?.name}</span> the owner of this customer? The owner
            flag moves off the current owner immediately.
          </p>
          {transfer.isError && <p className="text-xs text-ostrel-danger">{detail(transfer.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setTransferTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={transfer.isPending}
              onClick={() => transferTarget && transfer.mutate(transferTarget.unique_id)}
            >
              {transfer.isPending ? 'Transferring…' : 'Transfer ownership'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
